/**
 * InstanceTabs — the EIA portfolio's two views of its instances:
 *   Instance Stats        — the instance table (usage figures per instance);
 *   Self Perf Assessment  — the customer's own benchmark results, from the
 *                           optional self-perf (UDF) file upload.
 *
 * Port of legacy views/ManagePortfolio/Instances.vue (:2-14, :67-73). Legacy
 * shows this bar for EIA only and only once the portfolio has instances; the
 * page decides that and renders this only then.
 *
 * Built on the design system's Tabs (white label + teal underline on the
 * active tab — the app's design). Both panels stay MOUNTED while hidden, so
 * switching tabs never resets the instance table (its page, ticks, an open
 * editor).
 *
 * `aside(activeTab)` renders on the right of the tab row — the Operational
 * Safety Margin slider goes there, on the Instance Stats tab only.
 */
import { useState } from 'react';

import { ServerIcon } from '@components/common/icons';
import { EmptyState, Tabs } from '@components/ui';

import styles from './InstanceTabs.module.css';

export const INSTANCE_TABS = Object.freeze({
  STATS: 'instance-stats',
  SELF_PERF: 'self-perf',
});

/**
 * No self-perf rows yet. The text says WHERE they come from: a Self Perf
 * Assessment file can only be uploaded TOGETHER with an instances file (legacy
 * keeps its picker disabled until one is chosen; the server reads both and
 * returns the rows for both tabs) — so there is deliberately no upload button
 * here, which would have nothing to attach the file to.
 */
function SelfPerfEmpty() {
  return (
    <EmptyState
      className={styles.empty}
      icon={<ServerIcon />}
      title="No self performance data yet"
      description="Self Perf Assessment results arrive with a file upload: choose your instances file and a Self Perf Assessment file together in Upload File."
    />
  );
}

export function InstanceTabs({ instanceStats, selfPerf, aside }) {
  const [active, setActive] = useState(INSTANCE_TABS.STATS);

  return (
    <div className={styles.instanceTabs}>
      {aside ? <div className={styles.aside}>{aside(active)}</div> : null}
      <Tabs
        aria-label="Instance views"
        value={active}
        onValueChange={setActive}
        items={[
          { id: INSTANCE_TABS.STATS, label: 'Instance Stats', content: instanceStats },
          {
            id: INSTANCE_TABS.SELF_PERF,
            label: 'Self Perf Assessment',
            content: selfPerf ?? <SelfPerfEmpty />,
          },
        ]}
      />
    </div>
  );
}

export default InstanceTabs;
